import { NgModule } from '@angular/core';
import { Routes, RouterModule } from '@angular/router';
import { AdminLayout } from './layout/admin-layout/admin-layout';
import { TenantLayout } from './layout/tenant-layout/tenant-layout';
import { AuthGuard } from './shared/guards/auth.guard';
import { PermissionGuard } from './shared/guards/permission.guard';
import { GuestGuard } from './shared/guards/guest.guard';
import { SelectivePreloadStrategy } from './shared/strategies/selective-preload.strategy';
const routes: Routes = [
	// Default landing route. Dashboard is reachable by every role, and AuthGuard
	// bounces anonymous users to the right login (tenant or admin) from here.
	{ path: '', redirectTo: 'dashboard', pathMatch: 'full' },

	// Admin login route
	{
		path: 'admin/login',
		loadChildren: () =>
			import('./auth/auth.module').then(m => m.AuthModule),
		canActivate: [GuestGuard],
		data: { title: 'Admin Login' }
	},

	// Tenant login route (lazy-loaded AuthModule)
	{
		path: 'login',
		loadChildren: () =>
			import('./auth/auth.module').then(m => m.AuthModule),
		canActivate: [GuestGuard],
		data: { title: 'Login' }
	},
	{
		path: 'mfa-setup',
		loadComponent: () =>
			import('./auth/components/mfa-setup/mfa-setup').then(m => m.MfaSetup),
		canActivate: [GuestGuard],
		data: { title: 'MFA Setup' }
	},
	{
		path: 'mfa-verification',
		loadComponent: () =>
			import('./auth/components/mfa-verification/mfa-verification').then(m => m.MfaVerification),
		canActivate: [GuestGuard],
		data: { title: 'MFA Verification' }
	},
	{
		path: 'form-pdf/:id',
		loadComponent: () =>
			import('./create-new-form/components/form-pdf-view/form-pdf-view.component')
				.then(m => m.FormPdfViewComponent),
		data: { allowPublicToken: true, title: 'Form PDF' }
	},
	// Protected layout and its children (lazy-loaded feature modules)
	{
		path: '',
		component: TenantLayout,
		canActivate: [AuthGuard],
		canActivateChild: [AuthGuard],
		children: [
			{
				path: 'dashboard',
				loadChildren: () =>
					import('./dashboard/dashboard.module').then(m => m.DashboardModule),
				data: { preload: true, title: 'Dashboard' }
			},
			{
				path: 'create-new-form',
				loadChildren: () =>
					import('./create-new-form/create-new-form.module').then(m => m.CreateNewFormModule),
				canActivate: [],
				data: { permission: 'create-new-form', title: 'Create Form' },

			},
			{
				path: 'my-forms',
				loadChildren: () =>
					import('./my-forms/my-forms.module').then(m => m.MyFormsModule),
				canActivate: [PermissionGuard],
				data: { permission: 'my-forms', title: 'My Forms' }
			},
			{
				path: 'review-forms',
				loadChildren: () =>
					import('./review-forms/review-forms.module').then(m => m.ReviewFormsModule),
				canActivate: [PermissionGuard],
				data: { permission: 'forms-to-review', title: 'Review Forms' }
			},
			{
				path: 'form-history',
				loadChildren: () =>
					import('./form-history/form-history.module').then(m => m.FormHistoryModule),
				canActivate: [PermissionGuard],
				data: { permission: 'review-history', title: 'Form History' }
			},
			{
				path: 'department-management',
				loadChildren: () =>
					import('./department-management/department-management.module').then(m => m.DepartmentManagementModule),
				canActivate: [PermissionGuard],
				data: { permission: 'department-management', title: 'Department Management' }
			},
			{
				path: 'form-management',
				loadChildren: () =>
					import('./department-form-management/department-form-management.module').then(m => m.DepartmentFormManagementModule),
				canActivate: [PermissionGuard],
				data: { permission: 'form-management', title: 'Form Management' }
			},
			{
				path: 'role-management',
				loadChildren: () =>
					import('./role-management/role-management.module').then(m => m.RoleManagementModule),
				canActivate: [PermissionGuard],
				data: { permission: 'role', title: 'Role Management' }
			},
			{
				path: 'location-hierarchy',
				loadChildren: () =>
					import('./location-hierarchy/location-hierarchy.module').then(m => m.LocationHierarchyModule),
				canActivate: [PermissionGuard],
				data: { permission: 'location-hierarchy', title: 'Location Hierarchy' }
			},
			// {
			// 	path: 'fad-users',
			// 	loadChildren: () =>
			// 		import('./fad-users/fad-users.module').then(m => m.FadUsersModule),
			// 	canActivate: [PermissionGuard],
			// 	data: { permission: 'fad-users', title: 'FAD Users' }
			// },
			{
				path: 'user-management',
				loadChildren: () =>
					import('./user-management/user-management.module').then(m => m.UserManagementModule),
				canActivate: [PermissionGuard],
				data: { permission: 'user-management', title: 'User Management' }
			},
			// {
			// 	path: 'plan-management',
			// 	loadChildren: () =>
			// 		import('./tenant-plan/tenant-plan.module').then(m => m.TenantPlanModule),
			// 	canActivate: [PermissionGuard],
			// 	data: { permission: 'plan-management', title: 'Plan Management' }
			// },
			// {
			// 	path: 'billing-management',
			// 	loadChildren: () =>
			// 		import('./billing-management/billing-management-module').then(m => m.BillingManagementModule),
			// 	canActivate: [PermissionGuard],
			// 	data: { permission: 'billing-management', title: 'Billing Management' }
			// },
			{
				path: 'approver-setup',
				loadChildren: () =>
					import('./approver-setup/approver-setup-module').then(m => m.ApproverSetupModule),
				canActivate: [PermissionGuard],
				data: { permission: ['template-approver', 'pmr-manager'], title: 'Approver Setup' }
			},
			{
				path: 'approver-group',
				loadChildren: () =>
					import('./approver-group/approver-group-module').then(m => m.ApproverGroupModule),
				canActivate: [PermissionGuard],
				data: { permission: 'approver-group', title: 'Approval Groups' }
			},
			{
				path: 'customization',
				loadChildren: () =>
					import('./customization/customization.module').then(m => m.CustomizationModule),
				canActivate: [PermissionGuard],
				data: { permission: 'customization', title: 'Customization' }
			},
			{
				path: 'all-forms',
				loadChildren: () =>
					import('./all-forms/all-forms-module').then(m => m.AllFormsModule),
				canActivate: [PermissionGuard],
				data: { permission: 'all-forms', title: 'All Forms' }
			},
			{
				path: 'audit-log',
				loadChildren: () =>
					import('./admin-audit-log/admin-audit-log.module').then(m => m.AdminAuditLogModule),
				canActivate: [PermissionGuard],
				data: { permission: 'audit-trail', title: 'Audit Log' }
			},
			{
				path: 'advanced-search-setup',
				loadChildren: () =>
					import('./advanced-search-setup/advanced-search-setup-module').then(m => m.AdvancedSearchSetupModule),
				canActivate: [PermissionGuard],
				data: { permission: 'advanced-search-setup', title: 'Advanced Search Setup' }
			},
			{
				path: 'advanced-search',
				loadChildren: () =>
					import('./advanced-search/advanced-search-module').then(m => m.AdvancedSearchModule),
				canActivate: [PermissionGuard],
				data: { permission: 'advanced-search', title: 'Advanced Search' }
			},
			{
				path: 'user-ebs-information',
				loadChildren: () =>
					import('./user-ebs-information/user-ebs-information-module').then(m => m.UserEbsInformationModule),
				canActivate: [PermissionGuard],
				data: { permission: 'user-ebs-information', title: 'User EBS Information' }
			},
			// {
			// 	path: 'approval-setup',
			// 	loadChildren: () =>
			// 		import('./approval-setup/approval-setup-module').then(m => m.ApprovalSetupModule),
			// 	data: { title: 'Approval Setup' }
			// },
			{
				path: 'master-data-management',
				loadChildren: () =>
					import('./master-data-management/master-data-management-module').then(m => m.MasterDataManagementModule),
				canActivate: [PermissionGuard],
				data: { permission: 'master-data-management', title: 'Master Data Management' }
			},
			{
				path: 'unauthorized',
				loadComponent: () =>
					import('./shared/components/unauthorized/unauthorized').then(m => m.Unauthorized),
				data: { title: 'Access Denied' }
			}
			// No '**' here on purpose: this layout is mounted at path '', so a wildcard
			// child would swallow '/admin/...' before the admin layout below can match.
			// Unmatched tenant URLs fall through to the root wildcard at the end.
		]
	},

	// Admin protected layout and its children (lazy-loaded feature modules)
	{
		path: 'admin',
		component: AdminLayout,
		canActivate: [AuthGuard],
		canActivateChild: [AuthGuard],
		children: [
			{
				path: 'dashboard',
				loadChildren: () =>
					import('./dashboard/dashboard.module').then(m => m.DashboardModule),
				data: { preload: true, title: 'Admin Dashboard' }
			},
			{
				path: 'tenant-management',
				loadChildren: () =>
					import('./tenant-management/tenant-management.module').then(m => m.TenantManagementModule),
				canActivate: [PermissionGuard],
				data: { permission: 'tenant-management', title: 'Tenant Management' }
			},
			{
				path: 'plan-management',
				loadChildren: () =>
					import('./plan-management/plan-management.module').then(m => m.PlanManagementModule),
				canActivate: [PermissionGuard],
				data: { permission: 'plan-management', title: 'Plan Management' }
			},
			{
				path: 'billing-management',
				loadChildren: () =>
					import('./billing-management/billing-management-module').then(m => m.BillingManagementModule),
				canActivate: [PermissionGuard],
				data: { permission: 'billing-management', title: 'Billing Management' }
			},
			{
				path: 'admin-user-management',
				loadChildren: () =>
					import('./admin-user-management/admin-user-management.module').then(m => m.AdminUserManagementModule),
				canActivate: [PermissionGuard],
				data: { permission: 'user-management', title: 'Admin User Management' }
			},
			{
				path: 'admin-audit-log',
				loadChildren: () =>
					import('./admin-audit-log/admin-audit-log.module').then(m => m.AdminAuditLogModule),
				canActivate: [PermissionGuard],
				data: { permission: 'audit-trail', title: 'Admin Audit Log' }
			},
			{
				path: 'unauthorized',
				loadComponent: () =>
					import('./shared/components/unauthorized/unauthorized').then(m => m.Unauthorized),
				data: { title: 'Access Denied' }
			},
			// Unknown /admin path → admin dashboard.
			{ path: '**', redirectTo: 'dashboard' }
		]
	},

	// Wildcard for unmatched paths → dashboard. AuthGuard redirects anonymous users
	// to login and admin users on to /admin/dashboard from there.
	{ path: '**', redirectTo: '/dashboard' }
];

@NgModule({
	imports: [
		RouterModule.forRoot(routes, {
			preloadingStrategy: SelectivePreloadStrategy
		})
	],
	exports: [RouterModule]
})
export class AppRoutingModule { }
